import { useState } from 'react';
import { useMutation, useQuery } from 'convex/react';
import { api } from '../../../../convex/_generated/api';
import type { Doc, Id } from '../../../../convex/_generated/dataModel';
import { Button } from '../components/ui/button';
import {
  LANGUAGE_NAMES,
  STYLE_LOOK,
  type KidsLearning,
} from '../../../../packages/domain/src/kids';

/** What the panel needs of a studio episode. */
type KidsEpisodeSource = {
  _id: string;
  number: number;
  title: string;
  acceptedMasterId?: string;
  story?: { learning?: KidsLearning };
};
/**
 * The series studio's CLAPTAL Kids panel: each accepted episode published to the kids app with
 * what it teaches and its question. The writers' room proposed both; they can be corrected here.
 */
export function KidsPanel({
  projectId,
  episodes,
  writable,
  onError,
}: {
  projectId: string;
  episodes: KidsEpisodeSource[];
  writable: boolean;
  onError: (e: unknown) => void;
}) {
  const data = useQuery(api.kidsAdmin.forProject, { projectId: projectId as Id<'projects'> });
  if (!data) return null;
  const { series } = data;
  return (
    <details className="screenplay kids-panel" open>
      <summary>
        CLAPTAL Kids · {LANGUAGE_NAMES[series.language]} · {STYLE_LOOK[series.style].name} · ages{' '}
        {series.ageMin}–{series.ageMax} · {series.state}
      </summary>
      <p>
        Accepted episodes are published to CLAPTAL Kids only, never to the CLAPTAL feed.
        {data.translations.length
          ? ` Translations: ${data.translations.map((t) => LANGUAGE_NAMES[t.language]).join(', ')}.`
          : ''}
      </p>
      <ul className="kids-list">
        {episodes
          .slice()
          .sort((a, b) => a.number - b.number)
          .map((ep) => (
            <EpisodeRow
              key={ep._id}
              seriesId={series._id}
              episode={ep}
              published={data.episodes.find((k) => k.episodeId === ep._id)}
              writable={writable}
              onError={onError}
            />
          ))}
      </ul>
    </details>
  );
}

function EpisodeRow({
  seriesId,
  episode,
  published,
  writable,
  onError,
}: {
  seriesId: Id<'kidsSeries'>;
  episode: KidsEpisodeSource;
  published: Doc<'kidsEpisodes'> | undefined;
  writable: boolean;
  onError: (e: unknown) => void;
}) {
  const publish = useMutation(api.kidsAdmin.publishEpisode),
    unpublish = useMutation(api.kidsAdmin.unpublishEpisode);
  const proposed = episode.story?.learning;
  const [learning, setLearning] = useState<KidsLearning>(
    published?.learning ??
      proposed ?? { point: '', question: '', answers: ['', '', ''], correct: 0 },
  );
  const [busy, setBusy] = useState(false);
  const live = published?.state === 'published';
  const ready = !!episode.acceptedMasterId;
  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    try {
      await action();
    } catch (e) {
      onError(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <li className="kids-episode">
      <div>
        <strong>
          Episode {episode.number} · {episode.title}
        </strong>
        <small>
          {live
            ? 'Published to CLAPTAL Kids'
            : ready
              ? 'Accepted · not published'
              : 'Accept the episode before publishing it'}
        </small>
      </div>
      {ready && (
        <fieldset className="kids-learning" disabled={busy || !writable}>
          <label>
            What it teaches
            <input
              value={learning.point}
              maxLength={160}
              onChange={(e) => setLearning({ ...learning, point: e.target.value })}
            />
          </label>
          <label>
            Question
            <input
              value={learning.question}
              maxLength={160}
              onChange={(e) => setLearning({ ...learning, question: e.target.value })}
            />
          </label>
          {learning.answers.map((answer, i) => (
            <label key={i} className="kids-answer">
              <input
                type="radio"
                name={`correct-${episode._id}`}
                checked={learning.correct === i}
                onChange={() => setLearning({ ...learning, correct: i })}
                aria-label={`Answer ${i + 1} is right`}
              />
              <input
                value={answer}
                maxLength={60}
                aria-label={`Answer ${i + 1}`}
                onChange={(e) =>
                  setLearning({
                    ...learning,
                    answers: learning.answers.map((a, j) => (j === i ? e.target.value : a)),
                  })
                }
              />
            </label>
          ))}
          <div className="kids-actions">
            <Button
              onClick={() =>
                void run(() =>
                  publish({ seriesId, episodeId: episode._id as Id<'episodes'>, learning }),
                )
              }
            >
              {live ? 'Update on CLAPTAL Kids' : 'Publish to CLAPTAL Kids'}
            </Button>
            {live && (
              <Button
                variant="outline"
                onClick={() => void run(() => unpublish({ kidsEpisodeId: published._id }))}
              >
                Unpublish
              </Button>
            )}
          </div>
        </fieldset>
      )}
    </li>
  );
}
